import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";

import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={300}>
      {/* First tabbable element on the page, visible only when focused.
          Without it a keyboard user tabs the whole nav on every route. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        {t("nav.skipToContent")}
      </a>
      <Outlet />
      {/* One toast at a time. Errors persist (set per-toast in
          lib/query.ts); successes close themselves after 4s. */}
      <Toaster
        position="bottom-right"
        visibleToasts={1}
        duration={4000}
        closeButton
        toastOptions={{
          classNames: {
            // 20px of paint, 44px of target — sonner ships the close
            // button at `height: 20px; width: 20px`, less than half the
            // 44×44 house rule in docs/ui-contract.md, and it is exactly
            // the control a person reaches for one-handed (DEE-98).
            //
            // The same centred pseudo-element `Button`'s `sm` size uses,
            // and for the same reason: the button is positioned by
            // sonner's own CSS at the toast's top-left corner, so growing
            // it would push a 44px circle out over the toast rather than
            // enlarging a target in place. The extension covers the
            // toast's icon column, which is not interactive, and the empty
            // page outside it.
            //
            // No `relative` here — sonner already sets `position:
            // absolute` on this button, which is the containing block the
            // pseudo-element needs. Adding one would move the button.
            closeButton: [
              "after:absolute after:left-1/2 after:top-1/2",
              "after:size-11 after:-translate-x-1/2 after:-translate-y-1/2",
              "after:content-['']",
            ].join(" "),
          },
        }}
      />
    </TooltipProvider>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <main id="main" className="mx-auto flex max-w-md flex-col gap-4 px-4 py-24 text-center">
      <h1 className="text-xl font-semibold text-foreground">{t("errors.notFound.title")}</h1>
      <p className="text-sm text-muted">{t("errors.notFound.body")}</p>
      <div>
        <Button asChild variant="secondary" tooltip={t("tooltips.notFoundHome")}>
          <a href="/">{t("errors.notFound.action")}</a>
        </Button>
      </div>
    </main>
  );
}
